//
// NumericalInput
//
// A TextInput flanked by "−" and "+" buttons, separated from the value by vertical
// bars. Clicking adjusts the value by incrementStep; Shift multiplies by 5, Ctrl
// divides by 10.
//
import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
  useCallback,
  useMemo,
} from 'react';

import { appTooltipProps } from '../../utils';

function AdjustButton(props) {
  const {
    kind,
    disabled,
    onClick,
  } = props;

  return (
    <button
      onClick={onClick}
      id={kind}
      type="button"
      aria-label={(kind === 'plus') ? 'increase' : 'decrease'}
      disabled={disabled}
      className={classNames(
        'shrink-0 w-5 self-stretch text-lg font-bold leading-none select-none',
        {
          'text-pxPrimary': !disabled,
          'text-black/40 cursor-not-allowed': disabled,
        },
      )}
    >
      {(kind === 'plus') ? '+' : '−'}
    </button>
  );
}

AdjustButton.propTypes = {
  kind: PropTypes.oneOf(['plus', 'minus']).isRequired,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
};

function NumericalInput(props) {
  const {
    className,
    id,
    name,
    value,
    onChange,
    placeholder,
    invalid,
    disabled,
    tooltip,
    incrementStep = 1,
    positiveOnly,
    widthChars,
  } = props;

  const numericValue = parseFloat(value) || 0;
  const minusDisabled = disabled || (positiveOnly && (numericValue <= 0));

  const handleAdjust = useCallback(
    (event) => {
      if ((!onChange) || disabled) return;

      const increase = (event.currentTarget.id === 'plus');

      // Modifier keys: Shift = ×5, Ctrl = ÷10
      let stepMultiplier = 1;
      if (event.shiftKey) {
        stepMultiplier = 5;
      } else if (event.ctrlKey) {
        stepMultiplier = 0.1;
      }

      const step = incrementStep * stepMultiplier;
      const currentValue = parseFloat(value) || 0;
      let newValue = increase
        ? currentValue + step
        : currentValue - step;

      if (positiveOnly && (newValue < 0)) newValue = 0;

      // Round to 1 decimal place to avoid floating-point noise
      const roundedValue = Math.round(newValue * 10) / 10;

      onChange({
        currentTarget: {
          id,
          name,
          value: `${roundedValue}`,
        },
      });
    },
    [
      onChange, disabled, incrementStep, value, id, name, positiveOnly,
    ],
  );

  const handleOnChange = useCallback(
    (event) => {
      if (!onChange) return;
      if (positiveOnly) {
        const v = event.currentTarget.value;
        if ((v?.length) && (parseFloat(v) < 0)) {
          onChange({
            currentTarget: {
              id,
              name,
              value: v.replace(/^-+/, '') || '0',
            },
          });
          return;
        }
      }
      onChange(event);
    },
    [onChange, positiveOnly, id, name],
  );

  const divClassName = useMemo(
    () => classNames(
      'flex flex-row items-center relative rounded-md border',
      {
        'border-pxMainOutline': !invalid,
        'border-pxUiAlert': invalid,
        'opacity-80': disabled,
      },
      className,
    ),
    [className, invalid, disabled],
  );

  const txtClassName = useMemo(
    () => classNames(
      'outline-none resize-none overflow-hidden bg-transparent py-1 px-1 text-center',
      {
        'flex-1 min-w-0': (!widthChars),
        'text-black placeholder:text-black/40': !invalid,
        'text-pxUiAlert placeholder:text-pxUiAlert/50': invalid,
        'pointer-events-none': disabled,
      },
    ),
    [invalid, disabled, widthChars],
  );

  const labelClassName = useMemo(
    () => classNames(
      'absolute -top-2 left-2 px-1 text-xs bg-white text-nowrap',
      {
        'text-black/60': !invalid,
        'text-pxUiAlert': invalid,
      },
    ),
    [invalid],
  );

  const separatorClassName = useMemo(
    () => classNames(
      'self-stretch border-l',
      invalid ? 'border-pxUiAlert' : 'border-pxMainOutline',
    ),
    [invalid],
  );

  return (
    <div
      className={divClassName}
      {...appTooltipProps(tooltip)}
    >
      <AdjustButton kind="minus" onClick={handleAdjust} disabled={minusDisabled} />

      <div className={separatorClassName} />

      <input
        className={txtClassName}
        style={(widthChars) ? { width: `${widthChars + 1}ch` } : undefined}
        onChange={handleOnChange}
        value={value}
        placeholder={placeholder}
        id={id}
        name={name}
        disabled={disabled}
      />

      <div className={separatorClassName} />

      <AdjustButton kind="plus" onClick={handleAdjust} disabled={disabled} />

      {(value?.toString()?.length && placeholder)
        ? (
            <label htmlFor={id} className={labelClassName}>
              {placeholder}
            </label>
          )
        : null}
    </div>
  );
}

NumericalInput.propTypes = {
  className: PropTypes.string,
  id: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]).isRequired,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  invalid: PropTypes.bool,
  disabled: PropTypes.bool,
  tooltip: PropTypes.string,
  incrementStep: PropTypes.number,
  positiveOnly: PropTypes.bool,
  // Kept for backwards compatibility with callers that used the vertical arrow
  // layout; the +/- layout is visually unambiguous so this prop is ignored.
  swapControls: PropTypes.bool,
  // Field width in characters; the field grows with its box otherwise
  widthChars: PropTypes.number,
};

export default NumericalInput;
